import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchNotificationHistory } from '../notificationSlice';

const NotificationHistoryPanel = () => {
  const dispatch = useDispatch();
  const { history, loading, error } = useSelector(state => state.notification);
  const [filters, setFilters] = useState({
    status: '',
    trigger_type: '',
    recipient_type: ''
  });
  
  // Display limit
  const displayLimit = 50;

  useEffect(() => {
    dispatch(fetchNotificationHistory(filters));
  }, [dispatch, filters]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };


  // Display recent items only
  const displayItems = history.slice(0, displayLimit);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status, statusDisplay) => {
    const statusClasses = {
      pending: 'status-pending',
      sent: 'status-sent',
      failed: 'status-failed'
    };

    return (
      <span className={`status-badge ${statusClasses[status] || ''}`}>
        {statusDisplay}
      </span>
    );
  };

  const getTriggerBadge = (triggerType, triggerDisplay) => {
    const triggerClasses = {
      assignment_created: 'trigger-created',
      dday_3_notification: 'trigger-dday',
      dday_1_notification: 'trigger-dday',
      completion_notification: 'trigger-completion',
      reminder_notification: 'trigger-reminder'
    };

    return (
      <span className={`trigger-badge ${triggerClasses[triggerType] || ''}`}>
        {triggerDisplay}
      </span>
    );
  };

  const getRecipientBadge = (recipientType, recipientDisplay) => {
    const recipientClasses = {
      student: 'recipient-student',
      parent: 'recipient-parent'
    };

    return (
      <span className={`recipient-badge ${recipientClasses[recipientType] || ''}`}>
        {recipientDisplay}
      </span>
    );
  };

  return (
    <div className="history-panel">
      <div className="panel-header">
        <div className="header-content">
          <h3>발송 내역</h3>
          <p>발송된 알림톡의 상태와 내용을 확인할 수 있습니다. (최근 {displayLimit}개 표시)</p>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="filter-row">
          <div className="filter-group">
            <label>상태</label>
            <select 
              value={filters.status} 
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="filter-select"
            >
              <option value="">전체</option>
              <option value="pending">발송대기</option>
              <option value="sent">발송완료</option>
              <option value="failed">발송실패</option>
            </select>
          </div>

          <div className="filter-group">
            <label>트리거</label>
            <select 
              value={filters.trigger_type} 
              onChange={(e) => handleFilterChange('trigger_type', e.target.value)}
              className="filter-select"
            >
              <option value="">전체</option>
              <option value="assignment_created">과제배정</option>
              <option value="dday_3_notification">디데이 3일전 발송</option>
              <option value="dday_1_notification">디데이 1일전 발송</option>
              <option value="completion_notification">학습 완료 안내</option>
              <option value="reminder_notification">미완료 과제 리마인더</option>
            </select>
          </div>

          <div className="filter-group">
            <label>수신자</label>
            <select 
              value={filters.recipient_type} 
              onChange={(e) => handleFilterChange('recipient_type', e.target.value)}
              className="filter-select"
            >
              <option value="">전체</option>
              <option value="student">학생</option>
              <option value="parent">학부모</option>
            </select>
          </div>

        </div>
      </div>

      {/* History Table */}
      <div className="history-content">
        {loading.history ? (
          <div className="loading-message">
            발송 내역을 불러오는 중입니다...
          </div>
        ) : history.length === 0 ? (
          <div className="empty-message">
            <div className="empty-icon">📭</div>
            <h4>발송 내역이 없습니다</h4>
            <p>아직 발송된 알림톡이 없거나 검색 조건과 일치하는 내역이 없습니다.</p>
          </div>
        ) : (
          <div className="history-table-container">
            <table className="history-table">
              <thead>
                <tr>
                  <th>발송일시</th>
                  <th>학생</th>
                  <th>수신자</th>
                  <th>트리거</th>
                  <th>상태</th>
                  <th>과제명</th>
                </tr>
              </thead>
              <tbody>
                {displayItems.map((item) => (
                  <tr key={item.id}>
                    <td className="date-cell">
                      <div className="date-primary">{formatDate(item.sent_at || item.created_at)}</div>
                    </td>
                    <td className="student-cell">
                      <div className="student-name">{item.student_name}</div>
                    </td>
                    <td className="recipient-cell">
                      {getRecipientBadge(item.recipient_type, item.recipient_type_display)}
                    </td>
                    <td className="trigger-cell">
                      {getTriggerBadge(item.trigger_type, item.trigger_type_display)}
                    </td>
                    <td className="status-cell">
                      {getStatusBadge(item.status, item.status_display)}
                    </td>
                    <td className="assignment-cell">
                      {item.worksheet_title || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {error.history && (
        <div className="error-message">
          발송 내역을 불러오는데 실패했습니다: {typeof error.history === 'string' ? error.history : JSON.stringify(error.history)}
        </div>
      )}

      <style jsx>{`
        .history-panel {
          padding: 24px;
        }

        .panel-header {
          margin-bottom: 24px;
          padding-bottom: 20px;
          border-bottom: 1px solid #E6EDEC;
        }

        .header-content h3 {
          font-size: 20px;
          margin: 0 0 8px 0;
          color: #333;
        }

        .header-content p {
          font-size: 14px;
          color: #666;
          margin: 0;
        }

        .filters-section {
          margin-bottom: 24px;
          padding: 20px;
          background: #F4F8F7;
          border-radius: 8px;
        }

        .filter-row {
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
          align-items: end;
        }

        .filter-group {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .filter-group label {
          font-size: 13px;
          font-weight: 600;
          color: #333;
        }

        .filter-select {
          padding: 8px 12px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 14px;
          background: white;
        }

        .filter-select {
          min-width: 120px;
        }

        .filter-select:focus {
          outline: none;
          border-color: #0E141B;
          box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
        }

        .history-content {
          background: white;
          border-radius: 8px;
          border: 1px solid #E6EDEC;
          overflow: hidden;
        }

        .loading-message {
          text-align: center;
          padding: 60px 20px;
          color: #666;
          font-size: 16px;
        }

        .empty-message {
          text-align: center;
          padding: 60px 20px;
        }

        .empty-icon {
          font-size: 48px;
          margin-bottom: 16px;
        }

        .empty-message h4 {
          font-size: 18px;
          margin: 0 0 8px 0;
          color: #333;
        }

        .empty-message p {
          color: #666;
          margin: 0;
        }

        .history-table-container {
          overflow-x: auto;
        }

        .history-table {
          width: 100%;
          border-collapse: collapse;
        }

        .history-table th {
          background: #F4F8F7;
          padding: 16px 12px;
          text-align: left;
          font-weight: 600;
          color: #333;
          border-bottom: 2px solid #E6EDEC;
          font-size: 13px;
          white-space: nowrap;
        }

        .history-table td {
          padding: 16px 12px;
          border-bottom: 1px solid #E6EDEC;
          vertical-align: top;
        }

        .history-table tr:hover {
          background: #F4F8F7;
        }

        .date-cell {
          min-width: 140px;
        }

        .date-primary {
          font-size: 14px;
          color: #333;
          font-weight: 500;
        }

        .date-secondary {
          font-size: 12px;
          color: #666;
          margin-top: 2px;
        }

        .student-cell {
          min-width: 120px;
        }

        .student-name {
          font-size: 14px;
          color: #333;
          font-weight: 500;
        }


        .status-badge, .trigger-badge, .recipient-badge {
          display: inline-block;
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 500;
          white-space: nowrap;
        }

        .status-pending {
          background: #FBF4D9;
          color: #8A6D00;
          border: 1px solid #F5E9B8;
        }

        .status-sent {
          background: #E5FBF8;
          color: #00806B;
          border: 1px solid #CFF2EA;
        }

        .status-failed {
          background: #FCEDE9;
          color: #A93A25;
          border: 1px solid #F7DCD4;
        }

        .trigger-created {
          background: #E5FBF8;
          color: #1976d2;
          border: 1px solid #bbdefb;
        }

        .trigger-dday {
          background: #fff3e0;
          color: #ef6c00;
          border: 1px solid #ffcc02;
        }

        .trigger-completion {
          background: #e8f5e8;
          color: #2e7d32;
          border: 1px solid #a5d6a7;
        }

        .trigger-reminder {
          background: #FCEDE9;
          color: #c2185b;
          border: 1px solid #f8bbd9;
        }

        .recipient-student {
          background: #E5FBF8;
          color: #1976d2;
          border: 1px solid #bbdefb;
        }

        .recipient-parent {
          background: #f3e5f5;
          color: #7b1fa2;
          border: 1px solid #ce93d8;
        }

        .error-message {
          margin-top: 20px;
          padding: 12px 16px;
          background: #FCEDE9;
          border: 1px solid #F7DCD4;
          border-radius: 4px;
          color: #C2452D;
          font-size: 14px;
        }

        @media (max-width: 768px) {
          .filter-row {
            flex-direction: column;
            align-items: stretch;
          }

          .filter-group {
            width: 100%;
          }

          .history-table {
            font-size: 12px;
          }

          .history-table th,
          .history-table td {
            padding: 8px 6px;
          }
        }
      `}</style>
    </div>
  );
};

export default NotificationHistoryPanel;